import { Object3D, type SceneControl, use } from '@anov/3d-core'
import _ from 'lodash'
import type { IWeatherOption } from '../utils/type'
import { EWeatherType } from '../utils/type'
import Sky from '../objects/sky'
import Fog from '../objects/fog'
import Rain from '../objects/rain'

const _defaultOption: IWeatherOption = {
  sky: {
    position: [0, -2000, 0], // 天空盒的位置
    scale: [19990, 6000, 19990], // 大小
  },
  particle: {
    width: 2000, // 下雨、雪的面积
    height: 2000,
    segment: 5, // 分段数，控制雨、雪的效果
  },
}

export class WeatherSystem extends Object3D {
  private _time = '08:00'

  sky!: Sky
  fog!: Fog
  rain!: Rain
  eventListeners: Map<string, Function> = new Map()
  option: IWeatherOption = _.clone(_defaultOption)
  sceneControl: SceneControl

  constructor(option: IWeatherOption | undefined, sceneControl: SceneControl) {
    super()
    this.sceneControl = sceneControl
    _.mergeWith(this.option, option, this.customizer)
  }

  private customizer(objValue, srcValue) {
    if (_.isArray(objValue) && srcValue)
      return srcValue
  }

  async setWeather(weatherType = EWeatherType.Sunny, time = 12) {
    // 设置天空盒
    this.setSky(weatherType, time)

    switch (weatherType) {
      case EWeatherType.Foggy:
      case EWeatherType.Sand_Dust_Calm:
        this.stopEffect('rain', this.rain)
        this.setFog(weatherType)
        break
      case EWeatherType.Rain:
      case EWeatherType.Rain_Thunderstorm:
      case EWeatherType.Snow:
      case EWeatherType.Snow_Blizzard:
        this.stopEffect('fog', this.fog)
        this.setRain(weatherType)
        break
      default:
        this.stopEffect('rain', this.rain)
        this.stopEffect('fog', this.fog)
        break
    }
  }

  private setSky(type, time) {
    if (!this.sky) {
      this.sky = new Sky(this.option.sky)
      this.add(this.sky)

      const unSkyFrame = use.useframe(this.sceneControl, () => this.sky?.update())
      this.eventListeners.set('sky', unSkyFrame)
    }

    this.sky.setEffect(type, time)
  }

  setFog(type) {
    if (!this.fog) {
      this.fog = new Fog()
      const unFogFrame = use.useframe(this.sceneControl, () => this.fog.update())
      this.eventListeners.set('fog', unFogFrame)
      this.add(this.fog)
    }

    const fogSettings = {
      [EWeatherType.Foggy]: { color: [0.9, 0.9, 0.9], alpha: 0.4 },
      [EWeatherType.Sand_Dust_Calm]: { color: [1.0, 0.69, 0.48], alpha: 0.6 },
    }

    const { color, alpha } = fogSettings[type]
    this.fog.setColor(color)
    this.fog.setAlpha(alpha)
  }

  setRain(type) {
    const { particle } = this.option
    if (!this.rain) {
      this.rain = new Rain(particle, this.sceneControl)
      this.add(this.rain)
      const unRainFrame = use.useframe(this.sceneControl, () => this.rain?.update())
      this.eventListeners.set('rain', unRainFrame)
    }

    this.rain.setEffect(type)
  }

  setTime(time: number) {
    if (this.sky)
      this.sky.setTime(time)
  }

  stopEffect(key, effect) {
    if (effect) {
      effect.delete()
      this[key] = null
    }

    if (this.eventListeners.has(key)) {
      this.eventListeners.get(key)()
      this.eventListeners.delete(key)
    }
  }
}
